<template>
  <div class="yt-shell">
    <header class="yt-header">
      <div class="yt-header__inner">
        <!-- Left: menu + brand -->
        <div class="yt-header__left">
          <button
            type="button"
            class="yt-icon-btn"
            aria-label="Menu"
            @click="showDrawer"
          >
            <i class="fa-solid fa-bars" />
          </button>

          <router-link :to="HOME" class="yt-brand">
            <span class="yt-brand__mark" aria-hidden="true">
              <i class="fa-solid fa-play" />
            </span>
            <span class="yt-brand__name">{{ brandName }}</span>
            <sup class="yt-brand__region">{{ langLabelShort }}</sup>
          </router-link>
        </div>

        <!-- Center: search + location -->
        <div class="yt-header__center">
          <HeaderSearch />
        </div>

        <!-- Right: actions -->
        <div class="yt-header__right">
          <button
            type="button"
            class="yt-create"
            :title="$t('header.create')"
            @click="goPostNews"
          >
            <i class="fa-solid fa-plus" />
            <span class="yt-create__label">{{ $t('header.create') }}</span>
          </button>

          <button
            type="button"
            class="yt-room"
            :title="$t('header.manageRooms')"
            @click="goRooms"
          >
            <i class="fa-solid fa-house-circle-check" />
            <span class="yt-create__label">{{ $t('header.manageRooms') }}</span>
          </button>

          <!-- Logged in: points + notifications + avatar -->
          <template v-if="isLogin">
            <button
              type="button"
              class="yt-points-chip"
              :title="$t('header.pointsBalance', { points: balanceFull })"
              @click="goLoyaltyPoints"
            >
              <i class="fa-solid fa-coins" aria-hidden="true" />
              <span class="yt-points-chip__full">{{ balanceFull }}</span>
              <span class="yt-points-chip__compact">{{ balanceCompact }}</span>
            </button>

            <button
              type="button"
              class="yt-icon-btn yt-notif-btn"
              :title="$t('header.notifications')"
              @click="openNotifDrawer"
            >
              <i class="fa-regular fa-bell" />
              <span
                v-if="hasUnreadNotifications"
                class="yt-notif-btn__dot"
                aria-hidden="true"
              />
            </button>

            <button
              type="button"
              class="yt-avatar"
              :title="userTagName || $t('header.account')"
              @click="openUserDrawer"
            >
              {{ avatarInitial }}
            </button>
          </template>

          <!-- Guest: Sign in + more menu -->
          <template v-else>
             <GuestMoreMenu
              :lang-code="locale"
              :location-label="guestLocationLabel"
              @change-lang="setLang"
            />
            <button
              type="button"
              class="yt-signin"
              :title="$t('loginForm.login')"
              @click="goLogin"
            >
              <i class="fa-regular fa-circle-user" aria-hidden="true" />
              <span class="yt-signin__label">{{ $t('loginForm.login') }}</span>
            </button>

           
          </template>
        </div>
      </div>

      <div
        v-if="showMegaMenu"
        class="yt-mega"
        @mouseenter="keepMega"
        @mouseleave="hideMega"
      >
        <MegaMenu :mega-menu="megaMenu" />
      </div>
    </header>

    <Drawer placement="left" :show-form="showMenu" @closePopup="closePopup">
      <template #content>
        <MenuMobile
          :data-menu="menuDefault"
          :is-login="isLogin"
          :user-tag-name="userTagName"
          :has-unread="hasUnreadNotifications"
          :lang-label-short="langLabelShort"
          :hotline="companyHotline"
          @close="closePopup(false)"
          @open-notifications="openNotifDrawer"
          @open-account="openUserDrawer"
          @toggle-lang="toggleLang"
          @change-lang="setLang"
          @logged-out="refreshLogin"
        />
      </template>
    </Drawer>

    <DrawerCustomer
      v-model:open="showUserDrawer"
      :user-tag-name="userTagName"
      @logged-out="refreshLogin"
    />

    <DrawerNotification
      v-model:open="showNotifDrawer"
      v-model:has-unread="hasUnreadNotifications"
      :is-login="isLogin"
      @require-login="goLogin"
    />

    <PageBanner />
    <!-- <DefaultBreadcrumb /> -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCookies } from 'vue3-cookies'
import { STORE_INFORMATION, DEFAULT_MENU_HEADER } from '@/const/ApiRedis'
import { apiRedis } from '@/api/index'
import Drawer from '@/components/ant/drawer/Drawer.vue'
import MenuMobile from '@/components/Headers/default/menu/MenuMobile.vue'
import MegaMenu from '@/components/Headers/default/menu/MegaMenu.vue'
import HeaderSearch from '@/components/Headers/default/component/Search.vue'
import PageBanner from '@/components/ant/pagebaner/PageBanner.vue'
import DefaultBreadcrumb from '@/components/ant/breadcrumb/DefaultBreadcrumb.vue'
import DrawerCustomer from '@/components/Headers/default/customer/DrawerCustomer.vue'
import DrawerNotification from '@/components/Headers/default/customer/DrawerNotification.vue'
import GuestMoreMenu from '@/components/Headers/default/customer/GuestMoreMenu.vue'
import commons from '@/utils/Common'
import router from '@/router'
import store from '@/store'
import { LANGUAGE, DEFAULT } from '@/const/language'
import { HOME, LOGIN, userProfilePath, ROOM_MANAGER, CUSTOMER_POST_NEWS, LOYALTY_POINTS } from '@/const/path'
import axios from 'axios'
import { useLoyaltyPoints } from '@/composables/useLoyaltyPoints'

const { locale } = useI18n()
const { cookies } = useCookies()
const {
  balanceFull,
  balanceCompact,
  refresh: refreshPoints,
  fetchBalance: fetchPoints,
  connectSse,
  disconnectSse,
} = useLoyaltyPoints()

const menuDefault: any = ref([])
const companys: any = ref({})
const showMenu = ref(false)
const showNotifDrawer = ref(false)
const hasUnreadNotifications = ref(false)
const showMegaMenu = ref(false)
const megaMenu: any = ref([])
const isLogin = ref(false)
const showUserDrawer = ref(false)
const userTagName = ref('')
let megaTimer: ReturnType<typeof setTimeout> | null = null

const brandName = computed(
  () => companys.value?.subName || companys.value?.name || 'Kima Home'
)

const companyHotline = computed(
  () =>
    String(
      companys.value?.phone ||
        companys.value?.hotline ||
        companys.value?.mobile ||
        ''
    ).trim()
)

const langLabelShort = computed(() =>
  ((locale.value || cookies.get(LANGUAGE) || DEFAULT) === 'us' ? 'EN' : 'VN')
)

const guestLocationLabel = computed(() =>
  locale.value === 'us' ? 'Vietnam' : 'Việt Nam'
)

const avatarInitial = computed(() => {
  const name = (userTagName.value || 'H').trim()
  return name.charAt(0).toUpperCase() || 'H'
})

const openNotifDrawer = () => {
  refreshLogin()
  if (!isLogin.value) {
    router.push({ path: LOGIN }).catch(() => undefined)
    return
  }
  showNotifDrawer.value = true
}

const refreshLogin = () => {
  isLogin.value = !!commons.checkLogin()
  const user = commons.getUserData()
  userTagName.value = user?.tagName || user?.name || user?.username || ''
  if (isLogin.value) {
    refreshPoints()
    void fetchPoints()
    connectSse()
  } else {
    disconnectSse()
  }
}

const goLoyaltyPoints = () => {
  router.push({ path: LOYALTY_POINTS }).catch(() => undefined)
}

const loadMenuDefault = async () => {
  try {
    const res: any = await apiRedis(DEFAULT_MENU_HEADER)
    let data = res?.data?.data
    if (typeof data === 'string') data = JSON.parse(data)
    menuDefault.value = data || []
  } catch {
    menuDefault.value = []
  }
}

const loadCompanyInform = async () => {
  try {
    commons.ensureTenant()
    const res: any = await apiRedis(STORE_INFORMATION)
    const data = JSON.parse(res.data.data)
    const companyInfor = data[0]
    commons.setCokiesCompany(companyInfor)
    companys.value = companyInfor
  } catch {
    companys.value = commons.getCokiesCompany() || {}
  }
}

const showDrawer = () => {
  showMenu.value = true
}
const closePopup = (val: any) => {
  showMenu.value = val
}

const openUserDrawer = () => {
  showUserDrawer.value = true
}

const goLogin = () => router.push({ path: LOGIN })

const goPostNews = () => {
  refreshLogin()
  if (!isLogin.value) {
    router.push({ path: LOGIN })
    return
  }
  router.push({ path: CUSTOMER_POST_NEWS }).catch(() => undefined)
}

const goRooms = () => {
  refreshLogin()
  if (!isLogin.value) {
    router.push({ path: LOGIN })
    return
  }
  router.push({ path: ROOM_MANAGER }).catch(() => undefined)
}

const keepMega = () => {
  if (megaTimer) clearTimeout(megaTimer)
  showMegaMenu.value = true
}
const hideMega = () => {
  megaTimer = setTimeout(() => {
    showMegaMenu.value = false
  }, 180)
}

const setLang = (next: 'vn' | 'us') => {
  const current = locale.value || cookies.get(LANGUAGE) || DEFAULT
  if (current === next) return
  store.dispatch('setCountryChange', next)
  localStorage.setItem(LANGUAGE, next)
  cookies.set(LANGUAGE, next)
  locale.value = next
  axios.defaults.headers.common['Accept-Language'] = next
  loadMenuDefault()
}

const toggleLang = () => {
  const current = locale.value || cookies.get(LANGUAGE) || DEFAULT
  setLang(current === 'vn' ? 'us' : 'vn')
}

const isLoadLogin = computed(() => store.getters.isLoadLogin)
watch(isLoadLogin, () => refreshLogin())

onMounted(async () => {
  await loadMenuDefault()
  await loadCompanyInform()
  refreshLogin()
})
</script>

<style scoped>
.yt-shell {
  --yt-ink: #172033;
  --yt-muted: #64748b;
  --yt-border: #e2e8f0;
  --yt-surface: #f8fafc;
  --yt-white: #fff;
  --yt-accent: #0f3d5e;
  --yt-teal: #0e9f8a;
  --yt-cta: #D7B84D;
}

.yt-header {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: var(--yt-white);
}

.yt-header__inner {
  display: grid;
  grid-template-columns: auto minmax(220px, 720px) auto;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 8px 16px;
  min-height: 56px;
}

.yt-header__left,
.yt-header__right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.yt-header__right {
  justify-content: flex-end;
  position: relative;
}

.yt-header__center {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
}

.yt-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--yt-ink);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.yt-icon-btn:hover {
  background: var(--yt-surface);
}

.yt-notif-btn {
  position: relative;
}

.yt-notif-btn__dot {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e53935;
  border: 1.5px solid #fff;
  pointer-events: none;
}

.yt-brand {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: var(--yt-ink);
  min-width: 0;
}

.yt-brand__mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 20px;
  border-radius: 6px;
  background: var(--yt-accent);
  color: #fff;
  font-size: 10px;
}

.yt-brand__name {
  font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
  white-space: nowrap;
}

.yt-brand__region {
  font-size: 10px;
  font-weight: 500;
  color: var(--yt-muted);
  margin-left: 2px;
}

.yt-create {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  /* border: none; */
  border-radius: 18px;
  background: var(--yt-surface);
  color: var(--yt-ink);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  border: 1px solid var(--yt-accent);
}

.yt-create:hover {
  background: #e5e5e5;
}

.yt-create__label {
  white-space: nowrap;
}

.yt-room {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  /* border: none; */
  border-radius: 18px;
  background: var(--yt-accent);
  color: var(--yt-white);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  border: 1px solid var(--yt-accent);
}

.yt-room:hover {
  /* background: #e5e5e5; */
    font-weight: 700;

}

.yt-room__label {
  white-space: nowrap;
}

.yt-avatar {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: var(--yt-cta, #f59e0b);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.yt-points-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px 0 10px;
  border: 1px solid var(--yt-accent);
  border-radius: 5px;
  color: #ebc127;
  background: var(--yt-accent);
  font-size: 13px;
  font-weight: 400;
  cursor: pointer;
  flex-shrink: 0;
  letter-spacing: -0.01em;
}

/* .yt-points-chip:hover {
  background: linear-gradient(180deg, #fef3c7 0%, #fde68a 100%);
} */

.yt-points-chip i {
  color: #ebc127;
  font-size: 13px;
}

.yt-points-chip__compact {
  display: none;
}

.yt-signin {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #3ea6ff;
  border-radius: 18px;
  background: transparent;
  color: #3ea6ff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
}

.yt-signin:hover {
  background: rgba(62, 166, 255, 0.08);
}

.yt-signin i {
  font-size: 18px;
}

.yt-signin__label {
  line-height: 1;
}

.yt-cat-panel {
  min-width: 240px;
  max-height: 70vh;
  overflow: auto;
  padding: 8px;
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 12px;
}

.yt-cat-panel__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--yt-ink);
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.yt-cat-panel__item:hover {
  background: var(--yt-surface);
}

.yt-mega {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  z-index: 999;
  background: #fff;
  border-top: 1px solid #eee;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.12);
}

@media (max-width: 1100px) {
  .yt-header__inner {
    grid-template-columns: auto minmax(180px, 1fr) auto;
    gap: 12px;
  }
}

@media (max-width: 900px) {
  .yt-header__inner {
    grid-template-columns: auto 1fr auto;
    gap: 8px;
    padding: 8px 10px;
  }

  .yt-desktop-only {
    display: none !important;
  }

  .yt-brand__name {
    font-size: 16px;
  }

  .yt-create {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
    gap: 0;
  }

  .yt-create__label {
    display: none;
  }

  .yt-points-chip__full {
    display: none;
  }

  .yt-points-chip__compact {
    display: inline;
  }

  .yt-points-chip {
    padding: 0 10px;
    height: 32px;
  }

  .yt-signin {
    height: 34px;
    padding: 0 12px;
    font-size: 13px;
  }
}

@media (max-width: 768px) {
  .yt-header__inner {
    min-height: 52px;
    padding: 6px 8px;
  }

  .yt-icon-btn {
    width: 36px;
    height: 36px;
  }

  .yt-create {
    width: 36px;
    height: 36px;
  }

  .yt-brand__mark {
    width: 24px;
    height: 18px;
  }

  .yt-brand__name {
    font-size: 15px;
    max-width: 88px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .yt-brand__region {
    display: none;
  }
}

@media (max-width: 480px) {
  .yt-header__inner {
    gap: 4px;
    padding: 6px;
  }

  .yt-brand__name {
    max-width: 64px;
    font-size: 14px;
  }

  .yt-create {
    width: 32px;
    height: 32px;
    font-size: 13px;
  }

  .yt-avatar {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }

  .yt-signin {
    width: 36px;
    height: 36px;
    padding: 0;
    justify-content: center;
    border-radius: 50%;
    gap: 0;
  }

  .yt-signin__label {
    display: none;
  }
}
</style>
